import React, { useMemo } from 'react'
import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import { Case, Switch, When } from 'react-if'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { ParagraphSM } from '@/atoms/Text'
import { WatchMerchCard } from '@/organisms/WatchProjectHero/WatchMerchCard'
import { FranchiseExtended, FranchisePageData } from '@/services/FranchiseService'
import { Project, getProjectDescription } from '@/services/ProjectsService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'

export interface FranchiseHeroProps {
  className?: string
  slug: string
  isGuildMember: boolean
  pageData?: FranchisePageData | null
  defaultProject: Project
  franchise: FranchiseExtended
}

export const FranchiseHero: React.FC<FranchiseHeroProps> = ({
  className,
  slug,
  isGuildMember,
  pageData,
  defaultProject,
  franchise,
}) => {
  const shopifyStore = pageData?.shopifyStore
  const heroBackgroundVideo = pageData?.heroBackgroundVideo
  const heroBgImagePath = defaultProject?.title?.landscapeBackdropImage?.cloudinaryPath
  const mobileHeroBgImagePath = defaultProject?.title?.portraitBackdropImage?.cloudinaryPath
  const logoUrl =
    pageData?.logoOverride?.item?.[0]?.secure_url ??
    pageData?.logoOverride?.item?.[0]?.url ??
    franchise?.images?.[0]?.cloudinaryPath ??
    defaultProject?.title?.logo?.cloudinaryPath ??
    defaultProject?.logoCloudinaryPath
  const merchAnalytics = useMemo(() => {
    return { context: 'franchise', program: slug }
  }, [slug])

  const canShowMerch = Boolean(shopifyStore)
  const defaultProjectDescription = getProjectDescription(defaultProject)
  const primaryDescription = franchise?.description?.trim() || defaultProjectDescription

  return (
    <section
      id="hero"
      className="md:min-h-auto relative !h-fit min-h-[650px] w-full bg-cover text-white md:!h-[80vh] md:min-h-[650px] se:min-h-[665px] 3xl:min-h-[750px]"
    >
      <Switch>
        <Case condition={heroBackgroundVideo}>
          <video
            className="relative left-1/2 top-1/2 hidden h-full min-w-full -translate-x-1/2 -translate-y-1/2 bg-cover object-cover md:flex"
            autoPlay
            playsInline
            muted
            loop
            src={heroBackgroundVideo as string}
          />
          <video
            className="!aspect-h-9 !aspect-w-16 relative left-1/2 top-16 flex w-full min-w-full -translate-x-1/2 md:hidden"
            autoPlay
            playsInline
            muted
            loop
            src={heroBackgroundVideo as string}
          />
          <div
            className="absolute inset-0 hidden lg:flex"
            style={{
              background: 'linear-gradient(90deg, #151515 0%, #151515 17.48%, rgba(21, 21, 21, 0.00) 76.95%)',
            }}
          />
          <div
            className="absolute inset-0 hidden lg:flex"
            style={{
              background: 'linear-gradient(0deg, #151515 0%, rgba(21, 21, 21, 0.00) 100%)',
            }}
          />
          <div
            className="absolute inset-0 hidden md:flex lg:hidden"
            style={{
              background: 'linear-gradient(0deg, #151515 0%, rgba(21, 21, 21, 0.00) 100%)',
            }}
          />
          <div
            className="aspect-h-9 aspect-w-16 absolute left-1/2 top-16 flex h-fit min-w-full -translate-x-1/2 bg-cover object-cover md:hidden"
            style={{
              background: 'linear-gradient(0deg, #151515 0%, rgba(21, 21, 21, 0.00) 100%)',
            }}
          />
        </Case>
        <Case condition={heroBgImagePath}>
          <div className="relative left-1/2 top-1/2 h-full min-w-full -translate-x-1/2 -translate-y-1/2 bg-cover object-cover">
            <ResponsiveBackgroundImage
              src={getCloudinaryImageUrl({
                path: mobileHeroBgImagePath ?? heroBgImagePath ?? '',
              })}
              className="!block lg:!hidden"
            />
            <ResponsiveBackgroundImage
              src={getCloudinaryImageUrl({
                path: heroBgImagePath ?? '',
              })}
              className="!hidden lg:!block"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-core-gray-950" />
          </div>
        </Case>
      </Switch>
      <div className={classNames(['absolute top-0 left-0 h-fit md:h-full w-full', className])}>
        <div className="flex h-fit w-full max-w-[1680px] flex-col gap-4 md:h-full md:flex-row md:flex-wrap 3lg:mx-auto">
          <div className="relative mt-52 flex h-full w-full items-center px-4 md:mt-40 md:h-auto md:max-w-[650px] md:px-8 lg:mt-0 lg:px-16">
            <div className="flex flex-col gap-4">
              {logoUrl && (
                <img
                  alt="Franchise Logo Image"
                  className="w-auto !max-w-[167px] md:!max-w-[222px] lg:!max-w-[380px]"
                  src={getCloudinaryImageUrl({
                    path: logoUrl,
                    width: 380,
                    format: 'webp',
                    transforms: 'e_trim,q_auto:best',
                  })}
                />
              )}
              <ParagraphSM color="core-gray-400" className="lg:photon-paragraph-lg shortDesktop:photon-paragraph-md">
                {primaryDescription}
              </ParagraphSM>
            </div>
          </div>

          <div
            className="relative mx-auto mt-4 flex items-center sm:mt-0 md:mx-0 md:ml-auto md:items-end"
            id="corner-cta"
          >
            <When condition={canShowMerch}>
              {shopifyStore && (
                <Transition
                  show
                  appear
                  as="div"
                  enter="transition-opacity duration-1000"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity duration-1000"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="mx-4 mb-16 lg:mb-10">
                    <WatchMerchCard
                      storeUrl={shopifyStore.storeUrl ?? ''}
                      accessToken={shopifyStore.accessToken ?? ''}
                      handle={shopifyStore.handle ?? ''}
                      storeHref={shopifyStore.storeHref ?? ''}
                      analytics={merchAnalytics}
                      slug={slug}
                      isGuildMember={isGuildMember}
                    />
                  </div>
                </Transition>
              )}
            </When>
          </div>
        </div>
      </div>
    </section>
  )
}
